import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Trophy, MessageCircle, FileText, Video, Play, Download, Lock, Calendar, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MyChallenges = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoModal, setVideoModal] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (challengeId && challenges.length > 0) {
      const challenge = challenges.find(c => c.content_id === challengeId);
      if (challenge) {
        setSelectedChallenge(challenge);
      }
    }
  }, [challengeId, challenges]);

  const checkAuth = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      fetchMyChallenges(token, response.data);
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchMyChallenges = async (token, userData) => {
    setLoading(true);
    try {
      // Obtener todos los challenges
      const response = await axios.get(`${BACKEND_URL}/api/content?category=challenge`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filtrar solo los challenges a los que tiene acceso
      const purchasedChallenges = userData.purchased_challenges || [];
      const isPremium = userData.subscription_level === 'premium';

      const myChallenges = response.data.filter(challenge => 
        isPremium || purchasedChallenges.includes(challenge.content_id)
      );

      setChallenges(myChallenges);

      // Si hay un challengeId en la URL, seleccionarlo
      if (challengeId) {
        const challenge = myChallenges.find(c => c.content_id === challengeId);
        if (challenge) {
          setSelectedChallenge(challenge);
        }
      } else if (myChallenges.length === 1) {
        // Si solo tiene un challenge, seleccionarlo automáticamente
        setSelectedChallenge(myChallenges[0]);
      }
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFileUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('/api')) return `${BACKEND_URL}${path}`;
    return path;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  // Vista cuando no hay challenges
  if (challenges.length === 0) {
    return (
      <div className="min-h-screen bg-[#09090b] py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <Trophy className="w-20 h-20 text-zinc-300 mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4">No tienes challenges activos</h1>
          <p className="text-zinc-400 mb-8">
            Inscríbete a un challenge para comenzar tu transformación
          </p>
          <Link to="/">
            <Button className="bg-pink-600 hover:bg-pink-700 text-white rounded-full px-8 py-6">
              Ver Challenges Disponibles
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Vista de detalle de un challenge seleccionado
  if (selectedChallenge) {
    return (
      <div className="min-h-screen bg-[#09090b]">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-600 to-pink-500 text-white py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <button 
              onClick={() => {
                setSelectedChallenge(null);
                navigate('/my-challenges');
              }}
              className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
            >
              <ArrowLeft className="w-5 h-5" />
              Volver a mis challenges
            </button>
            <h1 className="text-3xl font-bold mb-2" style={{fontFamily: 'Syne, sans-serif'}}>
              {selectedChallenge.title}
            </h1>
            <p className="text-white/80 whitespace-pre-line">{selectedChallenge.description}</p>
            
            {selectedChallenge.start_date && (
              <div className="flex items-center gap-2 mt-4 text-white/80">
                <Calendar className="w-4 h-4" />
                <span>
                  {new Date(selectedChallenge.start_date).toLocaleDateString('es-ES', { 
                    day: 'numeric', month: 'long', year: 'numeric' 
                  })}
                  {selectedChallenge.end_date && (
                    <> - {new Date(selectedChallenge.end_date).toLocaleDateString('es-ES', { 
                      day: 'numeric', month: 'long', year: 'numeric' 
                    })}</>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Contenido */}
        <div className="max-w-4xl mx-auto py-8 px-4">
          {/* Link de Telegram */}
          {selectedChallenge.telegram_link && (
            <a 
              href={selectedChallenge.telegram_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl p-6 mb-6 hover:shadow-lg transition-shadow"
            >
              <div className="w-14 h-14 bg-zinc-900/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold">Grupo de Telegram</h3>
                <p className="opacity-80">Únete al grupo exclusivo del challenge</p>
              </div>
              <div className="text-2xl">→</div>
            </a>
          )}

          {/* Beneficios */}
          {selectedChallenge.challenge_benefits && (
            <div className="bg-zinc-900 rounded-2xl p-6 mb-6 border border-zinc-800">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Trophy className="w-6 h-6 text-pink-500" />
                Lo que incluye este challenge
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {selectedChallenge.challenge_benefits.split('\n').filter(l => l.trim()).map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-green-50 rounded-xl p-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-zinc-300">{benefit.replace(/^[•\-\*]\s*/, '')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Video principal del challenge */}
          {selectedChallenge.file_path && selectedChallenge.content_type === 'video' && (
            <div className="bg-zinc-900 rounded-2xl overflow-hidden mb-6 border border-zinc-800">
              <div className="p-4 border-b border-zinc-100">
                <h3 className="text-xl font-bold">Video del Challenge</h3>
              </div>
              <video 
                src={getFileUrl(selectedChallenge.file_path)}
                controls
                className="w-full aspect-video"
              />
            </div>
          )}

          {/* Materiales del Challenge */}
          {selectedChallenge.challenge_materials && selectedChallenge.challenge_materials.length > 0 && (
            <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-amber-500" />
                Materiales del Challenge
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedChallenge.challenge_materials.map((material, idx) => (
                  <div 
                    key={idx}
                    className="bg-[#09090b] rounded-xl p-4 flex items-center gap-4"
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      material.type === 'pdf' ? 'bg-red-100' :
                      material.type === 'video' ? 'bg-blue-100' : 'bg-green-100'
                    }`}>
                      {material.type === 'pdf' && <FileText className="w-6 h-6 text-red-500" />}
                      {material.type === 'video' && <Video className="w-6 h-6 text-blue-500" />}
                      {material.type === 'image' && <FileText className="w-6 h-6 text-green-500" />}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">{material.title}</h4>
                      <p className="text-sm text-zinc-500 capitalize">{material.type}</p>
                    </div>
                    {material.type === 'video' ? (
                      <button
                        onClick={() => setVideoModal(material)}
                        className="bg-blue-100 text-blue-600 p-2 rounded-lg hover:bg-blue-200"
                      >
                        <Play className="w-5 h-5" />
                      </button>
                    ) : (
                      <a
                        href={getFileUrl(material.file_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-zinc-200 text-zinc-300 p-2 rounded-lg hover:bg-zinc-300"
                      >
                        <Download className="w-5 h-5" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Modal de Video */}
        {videoModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <div className="bg-zinc-900 rounded-2xl w-full max-w-4xl overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-bold">{videoModal.title}</h3>
                <button 
                  onClick={() => setVideoModal(null)}
                  className="p-1 hover:bg-zinc-800 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <video 
                src={getFileUrl(videoModal.file_path)}
                controls
                autoPlay
                className="w-full aspect-video"
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vista de lista de challenges
  return (
    <div className="min-h-screen bg-[#09090b] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-2" style={{fontFamily: 'Syne, sans-serif'}}>
          Mis Challenges
        </h1>
        <p className="text-zinc-400 mb-8">Accede a todos los challenges que has comprado</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {challenges.map((challenge) => (
            <div 
              key={challenge.content_id}
              className="bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-800 hover:border-zinc-700 transition-shadow cursor-pointer"
              onClick={() => {
                setSelectedChallenge(challenge);
                navigate(`/my-challenges/${challenge.content_id}`);
              }}
            >
              {(challenge.cover_image || challenge.image_url) && (
                <img 
                  src={getFileUrl(challenge.cover_image || challenge.image_url)}
                  alt={challenge.title}
                  className="w-full h-40 object-cover"
                />
              )}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy className="w-5 h-5 text-pink-500" />
                  <span className="text-sm font-medium text-pink-600">Challenge Activo</span>
                </div>
                <h3 className="text-xl font-bold mb-2">{challenge.title}</h3>
                <p className="text-zinc-400 text-sm line-clamp-2 mb-4">{challenge.description}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-zinc-500">
                    {challenge.telegram_link && <MessageCircle className="w-4 h-4 text-blue-500" />}
                    {challenge.challenge_materials?.length > 0 && (
                      <span className="bg-zinc-800 px-2 py-1 rounded-full text-xs">
                        {challenge.challenge_materials.length} materiales
                      </span>
                    )}
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="rounded-full"
                  >
                    Ver Challenge →
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MyChallenges;
